import { Slot } from '@radix-ui/react-slot'
import {
  type LinkComponentProps,
  Link as RouterLink,
} from '@tanstack/react-router'
import * as React from 'react'
import { tv, type VariantProps } from 'tailwind-variants'

import { cn } from '@/utils'

interface RouterLinkProps
  extends LinkComponentProps,
    VariantProps<typeof linkVariants> {
  asChild?: boolean
  className?: string
  href?: never
  newTab?: boolean
}

interface HtmlLinkProps
  extends React.AnchorHTMLAttributes<HTMLAnchorElement>,
    VariantProps<typeof linkVariants> {
  asChild?: boolean
  to?: never
  newTab?: boolean
}

type LinkProps = RouterLinkProps | HtmlLinkProps

const linkVariants = tv({
  base: 'hover:underline cursor-pointer',
  variants: {
    theme: {
      default: 'text-foreground',
      brand: 'text-brand',
      danger: 'text-red-600',
    },
    size: {
      xs: 'underline-offset-2',
      sm: 'underline-offset-4',
      md: 'underline-offset-4',
    },
  },

  defaultVariants: { theme: 'default', size: 'md' },
})

const Link = React.forwardRef<HTMLAnchorElement, LinkProps>(
  (
    { asChild = false, className, children, size, theme, newTab, ...props },
    ref,
  ) => {
    const link = cn(linkVariants({ size, theme }), className)
    const newTabProps = newTab
      ? { rel: 'noopener noreferrer', target: '_blank' }
      : {}

    if (asChild) {
      return (
        <Slot className={link} ref={ref} {...newTabProps} {...props}>
          {children as React.ReactNode}
        </Slot>
      )
    }

    if ('to' in props) {
      return (
        <RouterLink className={link} ref={ref} {...newTabProps} {...props}>
          {children}
        </RouterLink>
      )
    }

    return (
      <a className={link} ref={ref} {...newTabProps} {...props}>
        {children as React.ReactNode}
      </a>
    )
  },
)
Link.displayName = 'Link'

export { Link, type LinkProps }
